import {
  Button,
  FormGroup,
  Icon,
  Input,
  Label,
  Margin,
  Size,
  Spinner,
  Title,
} from '@drawbotics/react-drylus';
import { useForm } from '@drawbotics/use-form';
import { useAppContext } from '@tokamakjs/react';
import { Fragment, useEffect, useState } from 'react';
import { AppContext } from 'src';
import { z } from 'zod';

import { PasswordValidation } from '~/components';
import { ResetPasswordController } from '~/routes/reset-password';
import { createTranslate, passwordSchema } from '~/utils';

export function ChangePassword(props: { controller: ResetPasswordController }) {
  const appContext = useAppContext<AppContext>();

  const [showPassword, setShowpassword] = useState<boolean>(false);
  const [showConfirmationPassword, setShowConfirmationPassword] = useState<boolean>(false);

  const tt = createTranslate('routes.reset_password.change_password_sidebar');

  const schema = z
    .object({
      password: passwordSchema,
      confirmPassword: z.string(),
    })
    .refine(
      (val) => {
        return val.password === val.confirmPassword;
      },
      {
        message: tt('no_match'),
        path: ['confirmPassword'],
      },
    );

  const form = useForm(schema, { defaultValues: { password: '', confirmPassword: '' } });

  /*
    WORKAROUND
    validate password and confirmPassword when writing, not just when unfocusing the input element

    this needs to be done asynchronously in useEffect and after the confirmPassword or password value changes,
    so that we use a new instance of validate() with the new form values in its closure
  */
  useEffect(() => {
    if (form.values.confirmPassword) {
      form.validate('confirmPassword');
    }
  }, [form.values.confirmPassword]);

  useEffect(() => {
    if (form.values.password) {
      form.validate('password');
    }
  }, [form.values.password]);

  return (
    <Fragment>
      <Title>{tt('title')}</Title>
      <Margin size={{ top: Size.DEFAULT }}>
        <FormGroup
          label={<Label>{tt('password')}</Label>}
          input={
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder={tt('password_placeholder')}
              name="password"
              value={form.get}
              onChange={form.set}
              validate={form.validate}
              error={!!form.getError('password')}
              trailing={
                <Button
                  tabIndex="-1"
                  leading={<Icon name="eye" />}
                  onClick={() => setShowpassword(!showPassword)}
                />
              }
            />
          }
        />
      </Margin>
      <Margin size={{ top: Size.DEFAULT }}>
        <PasswordValidation error={form.errors.password} />
      </Margin>
      <Margin size={{ top: Size.DEFAULT }}>
        <FormGroup
          label={<Label>{tt('confirm_password')}</Label>}
          input={
            <Input
              type={showConfirmationPassword ? 'text' : 'password'}
              placeholder={tt('confirm_password_placeholder')}
              name="confirmPassword"
              value={form.get}
              onChange={form.set}
              validate={form.validate}
              error={() => {
                /*
                  zod 'refine' method only runs after all fields match the object schema

                  this means users would only get a '"Passwords don't match" error if they
                  have everything filled in the form including the terms acceptance checkbox

                  To show the message in any context of the form,
                  we will check that the passwords match in case zod hasn't done it already
                */
                const zodError = form.getError('confirmPassword');

                if (zodError) {
                  return zodError;
                }

                return form.get('password') !== form.get('confirmPassword')
                  ? tt('no_match')
                  : undefined;
              }}
              trailing={
                <Button
                  tabIndex="-1"
                  leading={<Icon name="eye" />}
                  onClick={() => setShowConfirmationPassword(!showConfirmationPassword)}
                />
              }
            />
          }
        />
      </Margin>
      <Margin size={{ top: Size.EXTRA_LARGE }}>
        <Button
          disabled={props.controller.isChangingPassword}
          trailing={
            props.controller.isSendingResetRequest ? <Spinner size={Size.SMALL} inversed /> : null
          }
          size={Size.LARGE}
          color={appContext.color}
          onClick={(e) => {
            e.preventDefault();

            const validationResult = form.validateAll();

            if (validationResult.data) {
              props.controller.changePassword(validationResult.data);
            }
          }}>
          {tt('reset')}
        </Button>
      </Margin>
    </Fragment>
  );
}
